import { FocusMonitor } from '@angular/cdk/a11y';
import {
    Attribute,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Directive,
    ElementRef,
    EventEmitter,
    Host,
    Inject,
    Optional,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    MAT_EXPANSION_PANEL_DEFAULT_OPTIONS,
    MatExpansionPanel,
    MatExpansionPanelDefaultOptions,
    MatExpansionPanelHeader,
    matExpansionAnimations,
} from '@angular/material/expansion';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { AumExpansionPanel } from './expansion-panel';

@Component({
    selector: 'aum-expansion-panel-header',
    styleUrls: ['./expansion-panel-header.scss'],
    templateUrl: './expansion-panel-header.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    inputs: ['tabIndex'],
    animations: [matExpansionAnimations.indicatorRotate],
    host: {
        class: 'mat-expansion-panel-header mat-focus-indicator',
        role: 'button',
        '[attr.id]': 'panel._headerId',
        '[attr.tabindex]': 'tabIndex',
        '[attr.aria-controls]': '_getPanelId()',
        '[attr.aria-expanded]': '_isExpanded()',
        '[attr.aria-disabled]': 'panel.disabled',
        '[class.mat-expanded]': '_isExpanded()',
        '[class.aum-filled]': '_isFilled()',
        '[class.mat-expansion-toggle-indicator-after]': `_getTogglePosition() === 'after'`,
        '[class.mat-expansion-toggle-indicator-before]': `_getTogglePosition() === 'before'`,
        '[class._mat-animation-noopable]': '_animationMode === "NoopAnimations"',
        '[style.height]': '_getHeaderHeight()',
    },
})
export class AumExpansionPanelHeader extends MatExpansionPanelHeader {
    @Output() emitInformationData = new EventEmitter<{ information: string; saveToRichText: boolean }>();
    constructor(
        @Host() panel: AumExpansionPanel,
        element: ElementRef,
        focusMonitor: FocusMonitor,
        changeDetectorRef: ChangeDetectorRef,
        @Inject(MAT_EXPANSION_PANEL_DEFAULT_OPTIONS)
        @Optional()
        defaultOptions?: MatExpansionPanelDefaultOptions,
        @Optional() @Inject(ANIMATION_MODULE_TYPE) animationMode?: string,
        @Attribute('tabindex') tabIndex?: string,
    ) {
        const aumDefaultOptions: MatExpansionPanelDefaultOptions = {
            expandedHeight: '64px',
            collapsedHeight: '64px',
            hideToggle: defaultOptions ? defaultOptions.hideToggle : false,
        };
        super(
            panel as MatExpansionPanel,
            element,
            focusMonitor,
            changeDetectorRef,
            aumDefaultOptions,
            animationMode,
            tabIndex,
        );
    }

    /** Gets whether the panel is filled. */
    _isFilled(): boolean {
        return (this.panel as AumExpansionPanel).filled;
    }

    onEmitInformationData(result: { information: string; saveToRichText: boolean }): void {
        this.emitInformationData.emit(result);
    }
}

/**
 * `<aum-panel-title>`
 *
 * This directive is to be used inside of the MatExpansionPanelHeader component.
 */
@Directive({
    selector: 'aum-panel-title',
    host: {
        class: 'aum-expansion-panel-header-title',
    },
})
export class AumExpansionPanelTitle {}

/**
 * `<aum-panel-index>`
 *
 * This directive is to be used inside of the MatExpansionPanelHeader component.
 */
@Directive({
    selector: 'aum-panel-index',
    host: {
        class: 'aum-expansion-panel-header-index',
    },
})
export class AumExpansionPanelIndex {}

@Directive({
    selector: 'aum-expansion-panel-dragging-holder',
    host: {
        class: 'aum-expansion-panel-dragging-holder',
    },
})
export class AumExpansionDraggingHolder {}

@Directive({
    selector: 'aum-panel-actions',
    host: {
        class: 'aum-panel-actions',
    },
})
export class AumExpansionActions {}
